// IndexedDB-backed cache for the show runner's offline mode. Stores every
// piece/corps music blob plus a snapshot of the lineup data so the show can
// keep running if the venue wifi dies mid-festival.

const DB_NAME = 'iabf-showrunner-cache'
const DB_VERSION = 1
const TRACKS_STORE = 'tracks'
const META_STORE = 'meta'

function openDb () {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(TRACKS_STORE)) {
        db.createObjectStore(TRACKS_STORE, { keyPath: 'url' })
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE)
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function txPromise (tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error)
  })
}

export async function getTrack (url) {
  if (!url) return null
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(TRACKS_STORE, 'readonly')
    const req = tx.objectStore(TRACKS_STORE).get(url)
    req.onsuccess = () => resolve(req.result || null)
    req.onerror = () => reject(req.error)
  })
}

export async function putTrack (entry) {
  const db = await openDb()
  const tx = db.transaction(TRACKS_STORE, 'readwrite')
  tx.objectStore(TRACKS_STORE).put(entry)
  await txPromise(tx)
}

export async function getAllTrackUrls () {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(TRACKS_STORE, 'readonly')
    const req = tx.objectStore(TRACKS_STORE).getAllKeys()
    req.onsuccess = () => resolve(req.result || [])
    req.onerror = () => reject(req.error)
  })
}

export async function getAllTrackMeta () {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(TRACKS_STORE, 'readonly')
    const req = tx.objectStore(TRACKS_STORE).getAll()
    req.onsuccess = () => {
      const out = (req.result || []).map(r => ({
        url: r.url,
        sizeBytes: r.sizeBytes,
        fetchedAt: r.fetchedAt
      }))
      resolve(out)
    }
    req.onerror = () => reject(req.error)
  })
}

export async function deleteTrack (url) {
  const db = await openDb()
  const tx = db.transaction(TRACKS_STORE, 'readwrite')
  tx.objectStore(TRACKS_STORE).delete(url)
  await txPromise(tx)
}

export async function clearTracks () {
  const db = await openDb()
  const tx = db.transaction(TRACKS_STORE, 'readwrite')
  tx.objectStore(TRACKS_STORE).clear()
  await txPromise(tx)
}

export async function getSnapshot () {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(META_STORE, 'readonly')
    const req = tx.objectStore(META_STORE).get('snapshot')
    req.onsuccess = () => resolve(req.result || null)
    req.onerror = () => reject(req.error)
  })
}

export async function putSnapshot (snapshot) {
  const db = await openDb()
  const tx = db.transaction(META_STORE, 'readwrite')
  tx.objectStore(META_STORE).put(snapshot, 'snapshot')
  await txPromise(tx)
}

export async function clearAll () {
  const db = await openDb()
  const tx = db.transaction([TRACKS_STORE, META_STORE], 'readwrite')
  tx.objectStore(TRACKS_STORE).clear()
  tx.objectStore(META_STORE).clear()
  await txPromise(tx)
}

// Persistent-storage request prevents the browser from evicting the cache
// under disk pressure. Best-effort — Safari may ignore.
export async function requestPersistence () {
  try {
    if (navigator.storage && navigator.storage.persist) {
      return await navigator.storage.persist()
    }
  } catch (e) {
    // noop
  }
  return false
}

// Throttled fetch loop. `urls` is a list of music URLs to back up. `onProgress`
// gets called after each download with { done, total, bytesDone, succeeded,
// failed, currentUrl, lastError }. Skips URLs already present in the cache
// (cheap re-runs after lineup edits).
export async function downloadTracks (urls, { concurrency = 4, onProgress } = {}) {
  const unique = Array.from(new Set(urls.filter(Boolean)))
  const existing = new Set(await getAllTrackUrls())
  const pending = unique.filter(u => !existing.has(u))
  const total = unique.length
  let succeeded = unique.length - pending.length
  let failed = 0
  let bytesDone = 0
  const errors = []

  // Sum existing sizes so the progress bar starts at the right place.
  for (const m of await getAllTrackMeta()) {
    if (existing.has(m.url) && m.sizeBytes) bytesDone += m.sizeBytes
  }

  const queue = pending.slice()
  async function worker () {
    while (queue.length) {
      const url = queue.shift()
      if (!url) break
      if (onProgress) onProgress({ done: succeeded + failed, total, bytesDone, succeeded, failed, currentUrl: url })
      try {
        const res = await fetch(url, { cache: 'no-store' })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const blob = await res.blob()
        await putTrack({
          url,
          blob,
          sizeBytes: blob.size,
          fetchedAt: Date.now()
        })
        bytesDone += blob.size
        succeeded += 1
      } catch (e) {
        failed += 1
        const msg = (e && e.message) ? e.message : String(e)
        errors.push({ url, message: msg })
        console.error('Track backup failed:', url, e)
        if (onProgress) onProgress({ done: succeeded + failed, total, bytesDone, succeeded, failed, lastError: msg })
        continue
      }
      if (onProgress) onProgress({ done: succeeded + failed, total, bytesDone, succeeded, failed })
    }
  }
  const workers = Array.from({ length: Math.min(concurrency, pending.length || 1) }, () => worker())
  await Promise.all(workers)
  return { total, succeeded, failed, bytesDone, errors }
}

// Best-effort estimate of available storage quota.
export async function estimateStorage () {
  try {
    if (navigator.storage && navigator.storage.estimate) {
      return await navigator.storage.estimate()
    }
  } catch (e) {
    // noop
  }
  return null
}
